import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import TextM from "@common/components/text/TextM"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import { studentsService } from "@data/services/studentsService"
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { useDispatch } from "react-redux"
import { requiresPrerequisiteQuiz } from "../../../../engine/activity/prerequisite/prerequisiteGate"
import { apiCall, apiFailureOf } from "../../../../engine/shared/apiCall"

/**
 * « · Quiz d'abord · Retirer » : chez le masteur, à la suite du chapitre sous le titre de l'activité
 * d'un élève qu'un quiz de prérequis précède encore (celui de Sam au démarrage, ou celui que l'outil
 * du masteur allume par défaut). Discret à dessein (décision du 26/10) : il dit que le quiz passe
 * d'abord, et dépanne le masteur qui n'en veut pas. « Retirer » l'enlève (`DELETE
 * activities/{id}/quiz`) et, en live (`liveSessionId`), prévient l'élève par la salle (`newTodo`,
 * sans `forceFocus`) : sa liste se relit, et l'activité ouverte passe à l'énoncé. Rien une fois la
 * tentative faite : la correction de l'élève reste.
 */
const QuizFirstMention = ({
    activity,
    studentId,
    studentName,
    liveSessionId,
}) => {
    const dispatch = useDispatch()
    const { sendLiveKitData } = useLiveKitEventSender()
    const [removing, setRemoving] = useState(false)

    if (!requiresPrerequisiteQuiz(activity)) return null

    // La vue du masteur se relit tout de suite, sans attendre que la salle ait reçu le message.
    const refresh = () => {
        if (liveSessionId)
            dispatch(
                apiRequest(liveServiceV2.getLiveSessionV2, {
                    id: liveSessionId,
                })
            )
        else if (studentId)
            dispatch(apiRequest(studentsService.getStudent, { id: studentId }))
    }

    const remove = () => {
        if (removing) return
        setRemoving(true)
        apiCall(dispatch, activitiesService.deletePrerequisiteQuiz, {
            routeParams: { activityId: activity.id },
        })
            .then(async () => {
                refresh()
                const name = studentName || "l'élève"
                const told =
                    liveSessionId && studentId
                        ? await sendLiveKitData(
                              "newTodo",
                              { id: studentId, activity: { id: activity.id } },
                              [studentId]
                          )
                        : null
                // Le message n'a pas atteint la salle : l'élève passera à l'énoncé en rouvrant
                // l'activité (le quiz retiré n'existe plus pour lui).
                SnackBarM.show({
                    text1:
                        told?.ok === false
                            ? `Quiz retiré : ${name} verra l'énoncé en rouvrant son activité`
                            : `Quiz retiré : ${name} voit l'énoncé`,
                    duration: 3500,
                })
            })
            .catch((cause) => {
                refresh()
                SnackBarM.show({
                    text1:
                        apiFailureOf(cause)?.message ||
                        "Le quiz n'a pas pu être retiré, réessaie.",
                    duration: 4000,
                })
            })
            .finally(() => setRemoving(false))
    }

    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "center",
                flexShrink: 0,
            }}
            testID="quiz-first"
        >
            <TextM fontWeight="Light" fontSize={9} color={theme.colors.grey}>
                {/* Espaces insécables : en bout de texte, une espace ordinaire tombe à l'affichage. */}
                {"\u00a0·\u00a0Quiz d'abord\u00a0·\u00a0"}
            </TextM>
            <Pressable
                onPress={remove}
                disabled={removing}
                accessibilityRole="button"
                accessibilityLabel="Retirer le quiz"
                hitSlop={8}
                style={({ pressed }) => ({
                    opacity: pressed || removing ? 0.5 : 1,
                })}
            >
                <TextM
                    fontWeight="SemiBold"
                    fontSize={9}
                    color={theme.colors.primary}
                    style={{ textDecorationLine: "underline" }}
                >
                    Retirer
                </TextM>
            </Pressable>
        </View>
    )
}

export default QuizFirstMention
